import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { toEndOfUtcDay, toStartOfUtcDay } from '@chainflip/utils/date';
import { type UTCDate } from '@date-fns/utc';
import { subDays } from 'date-fns';
import { type LoanBorrowerType } from '@/shared/graphql/generated/lp/graphql';
import { type SwapRequestOverviewFragment } from '@/shared/graphql/generated/reporting/graphql';
import { createFilterStore } from '@/shared/hooks/createFilterStore';
import { type LoanStatus } from 'packages/block-explorer/helpers/lending';

// --- Swaps ---

export type SwapType =
  | 'isBoosted'
  | 'isDca'
  | 'isCcm'
  | 'isOnChain'
  | 'isVaultSwap'
  | 'isLiquidation';

export interface AllSwapsStoreData {
  startDate: UTCDate;
  endDate: UTCDate;
  swapStates: NonNullable<SwapRequestOverviewFragment['status']>[];
  assetsFrom: NonNullable<SwapRequestOverviewFragment['sourceAsset']>[];
  assetsTo: NonNullable<SwapRequestOverviewFragment['destAsset']>[];
  swapTypes: SwapType[];
  brokers: string[];
  refundAddress: string;
  destinationAddress: string;
  swapSizeRange: [number, number];
}

export const allSwapsInitialData: AllSwapsStoreData = {
  startDate: toStartOfUtcDay(subDays(new Date(), 29)),
  endDate: toEndOfUtcDay(new Date()),
  swapStates: [],
  assetsFrom: [],
  assetsTo: [],
  swapTypes: [],
  brokers: [],
  refundAddress: '',
  destinationAddress: '',
  swapSizeRange: [0, 0],
};

export const useAllSwapsFilterStore = createFilterStore(allSwapsInitialData);

// --- Loans ---

export interface AllLoansStoreData {
  startDate: UTCDate;
  endDate: UTCDate;
  assets: ChainflipAsset[];
  statuses: LoanStatus[];
  borrowerTypes: LoanBorrowerType[];
}

export const allLoansInitialData: AllLoansStoreData = {
  startDate: toStartOfUtcDay(subDays(new Date(), 29)),
  endDate: toEndOfUtcDay(new Date()),
  assets: [],
  statuses: [],
  borrowerTypes: [],
};

export const useAllLoansFilterStore = createFilterStore(allLoansInitialData);

// --- Lending ---

interface LendingStoreData {
  startDate: UTCDate;
  endDate: UTCDate;
  selectedLendingPools: ChainflipAsset[];
}

const lendingInitialData: LendingStoreData = {
  startDate: toStartOfUtcDay(subDays(new Date(), 29)),
  endDate: toEndOfUtcDay(new Date()),
  selectedLendingPools: [],
};

export const useLendingFilterStore = createFilterStore(lendingInitialData);
